<template>
    <rkm-modal
        extra-large
        ref="Modal"
        title="Atribuição de parâmetro por CBO/Especialidade"
    >
        <rkm-form>
            <rkm-row>
                <rkm-col cols="12">
                    <rkm-form-input
                        :validations="validations.tempoMedioPrevistoAtendimentoDias"
                        label="Tempo médio previsto para atendimento (dias)"
                        number
                        tooltip-top="Informe o tempo médio de espera, que o paciente aguardará para ser atendido."
                        v-model="model.tempoMedioPrevistoAtendimentoDias"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-area
            no-margin-top
            title="CBOs"
        >
            <rkm-table
                :fields="fields.ocupacoes"
                :items="ocupacoes"
                @filter-changed="handleOcupacoesFilterChanged"
                @filtered="ocupacoesFiltered = $event"
                sort-by="nome"
            >
                <template v-slot:head(checkboxes)>
                    <rkm-form-checkbox
                        :indeterminate="indeterminateOcupacoes"
                        :value="true"
                        @change="checkUncheckAllOcupacoes"
                        v-model="checkedAllOcupacoes"
                    />
                </template>

                <template v-slot:cell(checkboxes)="{item}">
                    <rkm-form-checkbox
                        :value="true"
                        @change="handleCheckedAllOcupacoes"
                        v-model="item.selecionado"
                    />
                </template>
            </rkm-table>
        </rkm-area>

        <rkm-area title="Especialidades">
            <rkm-table
                :fields="fields.especialidades"
                :items="especialidades"
                @filter-changed="handleEspecialidadesFilterChanged"
                @filtered="especialidadesFiltered = $event"
            >
                <template v-slot:head(checkboxes)>
                    <rkm-form-checkbox
                        :indeterminate="indeterminateEspecialidades"
                        :value="true"
                        @change="checkUncheckAllEspecialidades"
                        v-model="checkedAllEspecialidades"
                    />
                </template>

                <template v-slot:cell(checkboxes)="{item}">
                    <rkm-form-checkbox
                        :value="true"
                        @change="handleCheckedAllEspecialidades"
                        v-model="item.selecionado"
                    />
                </template>
            </rkm-table>
        </rkm-area>

        <template v-slot:footer="{cancel}">
            <rkm-button
                :disabled="!!loading"
                @click="cancel"
                color="secondary"
            >
                Fechar
            </rkm-button>

            <rkm-button
                :disabled="!!loading || disableAtribuir"
                @click="handleSave"
                color="primary"
            >
                Atribuir
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
// Mixins.
import { error, repository, stringCase, data } from '@/mixins'

// Vuex.
import { mapActions, mapGetters } from 'vuex'

export default {
    computed: {
        ...mapGetters({ loading: 'getLoading' }),
        fields() {
            return {
                ocupacoes: [
                    { key: 'checkboxes', label: '#', class: 'col-min text-center' },
                    { key: 'codigo', label: 'Código', sortable: true },
                    { key: 'nome', label: 'Nome', sortable: true },
                ],
                especialidades: [
                    { key: 'checkboxes', label: '#', class: 'col-min text-center' },
                    { key: 'nome', label: 'Nome', sortable: true },
                ],
            }
        },
        validations() {
            return {
                tempoMedioPrevistoAtendimentoDias: { max: 4, min_value: 1 },
            }
        },
        disableAtribuir() {
            return this.isNullOrEmptyString(this.model.tempoMedioPrevistoAtendimentoDias)
                || (
                    this.especialidades.filter(e => e.selecionado).length === 0
                    && this.ocupacoes.filter(o => o.selecionado).length === 0
                )
        },
    },
    created() {
        this.repositories = {
            especialidade: this.getConectaSusRepository('cadastro.especialidade')
        }
    },
    data: () => ({
        checkedAllOcupacoes: false,
        indeterminateOcupacoes: false,
        ocupacoes: [],
        ocupacoesFiltered: [],
        checkedAllEspecialidades: false,
        indeterminateEspecialidades: false,
        especialidades: [],
        especialidadesFiltered: [],
        model: {
            tempoMedioPrevistoAtendimentoDias: null,
        },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open() {
            this.reset()
            this.$refs.Modal.show()
            this.load()
        },
        load() {
            this.setLoading()

            this.repositories.especialidade.getDadosParaAtribuicao()
                .then(({ data }) => {
                    this.ocupacoes = data.ocupacoes
                        .map(ocupacao => ({ ...ocupacao, selecionado: false }))

                    this.especialidades = data.especialidades
                        .map(especialidade => ({ ...especialidade, selecionado: false }))
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleSave() {
            if (this.model.tempoMedioPrevistoAtendimentoDias !== null) {
                this.showConfirmation(
                    `Caso alguma especialidade já possuir um tempo médio previsto para atendimento
                    cadastrado, o valor informado será atualizado. Deseja continuar?`,
                    async () => this.save()
                )

                return
            }

            this.save()
        },
        save() {
            this.setLoading()

            this.repositories.especialidade.saveAtribuicao(this.prepareData())
                .then(() => {
                    this.showSaveNotification()
                    this.exit()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        prepareData() {
            const data = {
                ocupacoes: this.ocupacoes.filter(o => o.selecionado).map(o => o.id),
                especialidades: this.especialidades.filter(e => e.selecionado).map(e => e.id),
            }

            if (this.model.tempoMedioPrevistoAtendimentoDias !== null) {
                data.tempoMedioPrevistoAtendimentoDias = this.model.tempoMedioPrevistoAtendimentoDias
            }

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        reset() {
            this.ocupacoes = []
            this.especialidades = []
            this.model.tempoMedioPrevistoAtendimentoDias = null
        },
        exit() {
            this.$refs.Modal.hide()
        },
        handleOcupacoesFilterChanged() {
            this.checkedAllOcupacoes = false
            this.indeterminateOcupacoes = false
            this.ocupacoes.map(o => o.selecionado = false)
        },
        handleCheckedAllOcupacoes() {
            this.checkedAllOcupacoes = false
            this.indeterminateOcupacoes = false

            const ocupacoesCount = this.ocupacoesFiltered.length

            const selectedOcupacoesCount = this.ocupacoes
                .filter(o => this.ocupacoesFiltered.find(of => of.id === o.id))
                .filter(o => o.selecionado === true)
                .length

            if (selectedOcupacoesCount > 0) {
                if (selectedOcupacoesCount === ocupacoesCount) {
                    this.checkedAllOcupacoes = true
                } else  {
                    this.indeterminateOcupacoes = true
                }
            }
        },
        checkUncheckAllOcupacoes(selecionado) {
            this.checkedAllOcupacoes = selecionado
            this.indeterminateOcupacoes = false

            this.ocupacoes
                .filter(o => this.ocupacoesFiltered.find(of => of.id === o.id))
                .map(o => o.selecionado = selecionado)
        },
        handleEspecialidadesFilterChanged() {
            this.checkedAllEspecialidades = false
            this.indeterminateEspecialidades = false
            this.especialidades.map(e => e.selecionado = false)
        },
        handleCheckedAllEspecialidades() {
            this.checkedAllEspecialidades = false
            this.indeterminateEspecialidades = false

            const especialidadesCount = this.especialidadesFiltered.length

            const selectedEspecialidadesCount = this.especialidades
                .filter(e => this.especialidadesFiltered.find(ef => ef.id === e.id))
                .filter(e => e.selecionado === true)
                .length

            if (selectedEspecialidadesCount > 0) {
                if (selectedEspecialidadesCount === especialidadesCount) {
                    this.checkedAllEspecialidades = true
                } else  {
                    this.indeterminateEspecialidades = true
                }
            }
        },
        checkUncheckAllEspecialidades(selecionado) {
            this.checkedAllEspecialidades = selecionado
            this.indeterminateEspecialidades = false

            this.especialidades
                .filter(e => this.especialidadesFiltered.find(ef => ef.id === e.id))
                .map(e => e.selecionado = selecionado)
        },
    },
    mixins: [error, repository, stringCase, data],
    watch: {
        'ocupacoes'(value) {
            this.checkedAllOcupacoes = false
            this.indeterminateOcupacoes = false
            this.ocupacoesFiltered = value
        },
        'especialidades'(value) {
            this.checkedAllEspecialidades = false
            this.indeterminateEspecialidades = false
            this.especialidadesFiltered = value
        },
    }
}
</script>